import { useUnit } from 'effector-react';
import { Layers, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  $detail,
  $positions,
  $selectedId,
  nextLevelOf,
  positionSelected,
} from '../../entities/position';
import { inspectorOpened } from '../../entities/preferences';
import { openPositionRequested } from '../../features/open-position';
import { fillDialogOpened } from '../../features/record-fill';
import { useHotkeys } from '../../shared/lib/hotkeys';
import { shortcutOf } from '../../shared/lib/shortcuts';
import { routes } from '../../shared/routing';
import { Button, EmptyState, Shortcut, Spinner, Tooltip } from '../../shared/ui';
import { AppShell } from '../../widgets/app-shell';
import { MarketStrip } from '../../widgets/market-strip';
import { PositionCard } from '../../widgets/position-card';
import { PositionDetail } from '../../widgets/position-detail';

export function PositionsPage() {
  const { t } = useTranslation();
  const [positions, selectedId, detail, select, openInspector] = useUnit([
    $positions,
    $selectedId,
    $detail,
    positionSelected,
    inspectorOpened,
  ]);
  const list = positions ?? [];
  const index = list.findIndex((p) => p.id === selectedId);

  useHotkeys({
    j: () => {
      const next = list[Math.min(list.length - 1, index + 1)];
      if (next) select(next.id);
    },
    k: () => {
      const prev = list[Math.max(0, index - 1)];
      if (prev) select(prev.id);
    },
    enter: () => {
      if (selectedId !== null) void routes.position.open({ id: String(selectedId) });
    },
    f: () => {
      const selected = list[index];
      const level = selected ? nextLevelOf(selected) : undefined;
      if (selected && level) fillDialogOpened({ position: selected, level: level.n });
    },
  });

  const inspector =
    detail !== null && detail.id === selectedId ? (
      <PositionDetail
        compact
        detail={detail}
        onClose={() => {
          select(null);
        }}
      />
    ) : null;

  return (
    <AppShell
      page="positions"
      crumbs={[t('nav.positions')]}
      inspector={inspector}
      onInspectorClose={() => {
        select(null);
      }}
    >
      <div className="mx-auto w-full max-w-5xl space-y-4 px-6 py-6 max-sm:px-4">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-lg font-semibold">{t('nav.positions')}</h1>
          <Tooltip content={t('palette.newPosition')} shortcut={shortcutOf('newPosition')}>
            <Button
              variant="primary"
              leadingIcon={<Plus />}
              onClick={() => {
                openPositionRequested({});
              }}
            >
              {t('positions.new')}
            </Button>
          </Tooltip>
        </div>
        <MarketStrip />
        {positions === null ? (
          <div className="flex justify-center py-16">
            <Spinner label={t('common.loading')} />
          </div>
        ) : list.length === 0 ? (
          <EmptyState
            className="py-16"
            icon={<Layers />}
            title={t('positions.emptyTitle')}
            body={t('positions.emptyBody')}
            action={
              <Button
                variant="primary"
                leadingIcon={<Plus />}
                onClick={() => {
                  openPositionRequested({});
                }}
              >
                {t('positions.new')}
              </Button>
            }
          />
        ) : (
          <div className="space-y-3">
            <p className="flex items-center gap-1.5 text-xs text-text-3 max-lg:hidden">
              {t('positions.keyboardHint')}
              <Shortcut keys={shortcutOf('next')} />
              <Shortcut keys={shortcutOf('previous')} />
              <Shortcut keys={shortcutOf('open')} />
              <Shortcut keys={shortcutOf('recordFill')} />
            </p>
            {list.map((position) => (
              <PositionCard
                key={position.id}
                position={position}
                selected={position.id === selectedId}
                onSelect={() => {
                  select(position.id);
                  openInspector();
                }}
                onOpen={() => {
                  void routes.position.open({ id: String(position.id) });
                }}
              />
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
